/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import {
  Layers,
  Search,
  Plus,
  ExternalLink,
  Edit2,
  Filter,
} from "lucide-react";

export default function Sections() {
  const [sectionList, setSectionList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const { listOfAllSections } = useContext(APIContext);

  useEffect(() => {
    listOfAllSections()
      .then((sections) => {
        setSectionList(Array.isArray(sections) ? sections : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load sections");
        setSectionList([]);
        setLoading(false);
      });
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    sectionList.forEach((s) => {
      const cat = s.section_category || s.category;
      if (cat && typeof cat === "string") set.add(cat.toLowerCase());
    });
    return Array.from(set).sort();
  }, [sectionList]);

  const filteredSections = useMemo(() => {
    return sectionList.filter((section) => {
      const name = (section.section_name || "").toLowerCase();
      const slug = (section.section_slug || "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q || name.includes(q) || slug.includes(q);

      let matchesCat = true;
      if (selectedCategory !== "all") {
        const cat = (section.section_category || section.category || "").toLowerCase();
        matchesCat = cat === selectedCategory;
      }

      return matchesSearch && matchesCat;
    });
  }, [sectionList, searchQuery, selectedCategory]);

  return (
    <>
      <HelmetLayout title="Sections" />
      <div className="space-y-6 pb-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Sections</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Explore, preview, and manage your reusable UI sections library.
            </p>
          </div>
          <Link
            to="/sections/add"
            className="bg-[#623ce7] hover:bg-[#5030bc] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-[#623ce7]/20 flex items-center gap-2"
          >
            <Plus size={16} />
            Add New Section
          </Link>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Total Sections</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading ? "—" : sectionList.length}
              </h3>
            </div>
            <div className="p-3 bg-[#623ce7]/10 text-[#623ce7] rounded-xl">
              <Layers size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Categories</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading ? "—" : categories.length}
              </h3>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <Filter size={22} />
            </div>
          </div>
        </div>

        {/* Search & Category Filter Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
          {/* Full Width Search Bar */}
          <div className="relative w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search sections by name or slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
            />
          </div>

          {/* Dedicated Category Pills Row */}
          {categories.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide pt-1 border-t border-slate-100">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider shrink-0 mr-1">
                Category:
              </span>
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors shrink-0 ${
                  selectedCategory === "all"
                    ? "bg-[#623ce7] text-white shadow-sm shadow-[#623ce7]/30"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                All ({sectionList.length})
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize whitespace-nowrap transition-colors shrink-0 ${
                    selectedCategory === cat
                      ? "bg-[#623ce7] text-white shadow-sm shadow-[#623ce7]/30"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {cat.replace(/-/g, " ")}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sections Grid */}
        {loading ? (
          <div className="flex flex-col justify-center items-center py-24">
            <PulseLoader color="#623ce7" size={12} />
            <p className="text-slate-400 text-sm mt-3">Loading sections...</p>
          </div>
        ) : filteredSections.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500 shadow-sm">
            <Layers className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="font-semibold text-slate-700 text-base">No sections found</p>
            <p className="text-sm text-slate-400 mt-1">
              {searchQuery ? "Try refining your search query." : "Click 'Add New Section' to create one."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredSections.map((section) => {
              const sectionID = section.pk.replace("section-", "");

              return (
                <div
                  key={sectionID}
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#623ce7]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#623ce7]/5 flex flex-col"
                >
                  {/* Thumbnail & Hover Overlay */}
                  <div className="relative h-48 bg-slate-100 overflow-hidden">
                    <img
                      src={section.section_thumbnail || "https://picsum.photos/seed/placeholder/400/250"}
                      alt={section.section_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = "https://picsum.photos/seed/placeholder/400/250";
                      }}
                    />
                    <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-3">
                      <a
                        href={`https://${section.section_slug}.grigora.app`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2.5 bg-white rounded-full text-slate-900 hover:bg-slate-100 transition-colors shadow-md"
                        title="Preview Live Section"
                      >
                        <ExternalLink size={18} />
                      </a>
                      <Link
                        to={`/sections/${sectionID}`}
                        className="p-2.5 bg-[#623ce7] rounded-full text-white hover:bg-[#5030bc] transition-colors shadow-md"
                        title="Edit Section"
                      >
                        <Edit2 size={18} />
                      </Link>
                    </div>

                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-md text-xs font-mono text-slate-700 font-medium shadow-sm border border-slate-100">
                      {section.section_slug}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <Link
                        to={`/sections/${sectionID}`}
                        className="font-semibold text-slate-900 hover:text-[#623ce7] transition-colors text-base truncate block mb-1"
                        title={section.section_name}
                      >
                        {section.section_name}
                      </Link>
                      <p className="text-xs text-slate-400 font-mono mb-2">
                        {section.section_slug}.grigora.app
                      </p>

                      {section.section_category && (
                        <span className="inline-block px-2 py-0.5 bg-slate-100 rounded text-[11px] font-medium text-slate-600 border border-slate-200 capitalize">
                          {section.section_category}
                        </span>
                      )}
                    </div>

                    {/* Bottom Actions */}
                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2 mt-3">
                      <a
                        href={`https://${section.section_slug}.grigora.app`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors"
                      >
                        Live Preview <ExternalLink size={12} />
                      </a>
                      <Link
                        to={`/sections/${sectionID}`}
                        className="text-xs font-medium bg-[#623ce7]/10 hover:bg-[#623ce7]/20 text-[#623ce7] px-2.5 py-1 rounded transition-colors"
                      >
                        Edit
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
